/**
 * use-keyboard-viewport.ts — Bàn phím ảo trên điện thoại (nhất là iPhone: Safari / ứng dụng thêm vào màn hình chính).
 *
 * iOS KHÔNG co khung nhìn bố cục khi bàn phím mở (`100dvh` vẫn là chiều cao đầy đủ): trang bị bàn phím che nửa dưới rồi tự trượt lên để lộ ô đang gõ
 * → mất thanh tiêu đề, ô soạn lơ lửng giữa màn với khoảng trống phía dưới. Cách chữa: khi vùng nhìn thấy (`visualViewport`) thấp hơn khung bố cục quá
 * `KEYBOARD_MIN_PX`, ghi chiều cao thật vào `--app-vh` và độ lệch phía trên vào `--app-vt`, gắn `data-keyboard` lên <html>. CSS (index.css) khi đó GHIM khung
 * ứng dụng `position: fixed; top: var(--app-vt); height: var(--app-vh)` đúng vào vùng nhìn thấy.
 * Vì sao phải ghim: iOS "đẩy" vùng nhìn thấy lên (`visualViewport.offsetTop > 0`) để lộ ô đang gõ, và `window.scrollTo(0, 0)` KHÔNG hoàn lại được phần đẩy này —
 * khung chỉ co chiều cao thì tiêu đề trượt khuất lên trên, còn dưới ô soạn hở một khoảng trống (lỗi iPhone 12/10/2026). Bàn phím đóng → trả nguyên trạng.
 * Chỉ bật ở màn cần (chat chi tiết) — các trang khác giữ hành vi mặc định của trình duyệt.
 */
import { useEffect } from 'react'

/** Vùng nhìn thấy thấp hơn khung bố cục bấy nhiêu px trở lên mới coi là bàn phím mở (thanh địa chỉ co giãn chỉ ~50–100px). */
export const KEYBOARD_MIN_PX = 150

/** Hàm thuần: bàn phím có đang mở không, và chiều cao khung ứng dụng cần đặt. */
export function keyboardState(layoutHeight: number, visibleHeight: number, offsetTop = 0): { open: boolean; height: number; top: number } {
  const open = layoutHeight - visibleHeight >= KEYBOARD_MIN_PX
  return { open, height: Math.round(visibleHeight), top: Math.max(0, Math.round(offsetTop)) }
}

export function useKeyboardViewport(enabled: boolean): void {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    if (!enabled || !vv) return
    const root = document.documentElement
    const reset = () => {
      root.style.removeProperty('--app-vh')
      root.style.removeProperty('--app-vt')
      delete root.dataset.keyboard
    }
    const update = () => {
      const k = keyboardState(window.innerHeight, vv.height, vv.offsetTop)
      if (k.open) {
        root.style.setProperty('--app-vh', `${k.height}px`)
        root.style.setProperty('--app-vt', `${k.top}px`)
        root.dataset.keyboard = '1'
      } else {
        reset()
      }
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      reset()
    }
  }, [enabled])
}
